import { useMemo, useState } from "react";
import { ensureFullBomReplacementDraft } from "@/lib/manufacturing/ensureFullBomReplacementDraft";
import {
  applyRestartDraftToRecipe,
  copyTemplateBomToRecipe,
  lockDraftIdentity,
  seedRestartDraftFromDetail,
  wipeRecipeKeepingIdentity,
} from "@/lib/manufacturing/modelRestartActions";
import { getManufacturingRecipeDetail } from "@/lib/manufacturing/recipesClient";
import { correctBomFromTranscript } from "@/lib/manufacturing/recipesDictationClient";
import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";
import type { MfgMaterial, MfgRecipeDetailResponse, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingDictatedRecipeReview } from "./ManufacturingDictatedRecipeReview";
import {
  ManufacturingModelsRestartChooser,
  type RestartMode,
} from "./ManufacturingModelsRestartChooser";

type Phase =
  | { kind: "choose" }
  | { kind: "busy"; label: string }
  | { kind: "review"; draft: DictatedRecipeDraft; transcript: string; prior: DictatedRecipeDraft }
  | { kind: "error"; message: string };

function normalizeDraft(draft: DictatedRecipeDraft): DictatedRecipeDraft {
  return {
    ...draft,
    lines: draft.lines.map((ln) => ({
      ...ln,
      slot_label: normalizeSlotLabel(ln.slot_label),
    })),
  };
}

export function ManufacturingModelsRestartModal({
  detail,
  recipes,
  materials,
  orgSlotLabels = [],
  onClose,
  onDone,
}: {
  detail: MfgRecipeDetailResponse;
  recipes: readonly MfgRecipeSummary[];
  materials: ReadonlyArray<MfgMaterial>;
  orgSlotLabels?: readonly string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<RestartMode>("wipe");
  const [templateId, setTemplateId] = useState("");
  const [brief, setBrief] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "choose" });
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Notes from the template model when AI-adapting; applied on review save. */
  const [templateNotes, setTemplateNotes] = useState<string | null>(null);

  const templateOptions = useMemo(
    () =>
      recipes
        .filter((r) => r.id !== detail.recipe.id && r.is_active)
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code)),
    [recipes, detail.recipe.id],
  );

  async function runAiFromPrior(prior: DictatedRecipeDraft, transcript: string) {
    const trimmed = transcript.trim();
    if (!trimmed) return;
    setPhase({ kind: "busy", label: "Asking AI…" });
    const result = await correctBomFromTranscript({ priorDraft: prior, transcript: trimmed });
    if (result.ok && result.data.ok && result.data.draft) {
      const locked = lockDraftIdentity(
        ensureFullBomReplacementDraft(prior, result.data.draft),
        detail,
      );
      setPhase({
        kind: "review",
        draft: normalizeDraft(locked),
        transcript: trimmed,
        prior,
      });
      return;
    }
    setPhase({
      kind: "error",
      message: result.ok
        ? "AI could not produce a usable restart draft. Try rephrasing."
        : result.error || "AI restart failed",
    });
  }

  async function onContinue() {
    setSaveError(null);
    if (mode === "wipe") {
      setPhase({ kind: "busy", label: "Wiping…" });
      const res = await wipeRecipeKeepingIdentity(detail.recipe.id);
      if (!res.ok) {
        setPhase({ kind: "error", message: res.error });
        return;
      }
      onDone();
      return;
    }

    if (mode === "template") {
      if (!templateId) {
        setSaveError("Select a template model.");
        return;
      }
      const adapt = brief.trim();
      if (!adapt) {
        setPhase({ kind: "busy", label: "Copying template…" });
        const res = await copyTemplateBomToRecipe(detail.recipe.id, templateId);
        if (!res.ok) {
          setPhase({ kind: "error", message: res.error });
          return;
        }
        onDone();
        return;
      }
      setPhase({ kind: "busy", label: "Loading template…" });
      const src = await getManufacturingRecipeDetail(templateId);
      if (!src.ok) {
        setPhase({ kind: "error", message: src.error });
        return;
      }
      setTemplateNotes(src.data.recipe.notes ?? "");
      await runAiFromPrior(seedRestartDraftFromDetail(src.data), adapt);
      return;
    }

    const text = brief.trim();
    if (!text) {
      setSaveError("Describe what the AI should change.");
      return;
    }
    setTemplateNotes(null);
    await runAiFromPrior(seedRestartDraftFromDetail(detail), text);
  }

  async function onSaveReview(draft: DictatedRecipeDraft) {
    setSaveBusy(true);
    setSaveError(null);
    const res = await applyRestartDraftToRecipe(
      detail,
      lockDraftIdentity(draft, detail),
      templateNotes !== null ? { notes: templateNotes } : undefined,
    );
    setSaveBusy(false);
    if (!res.ok) {
      setSaveError(res.error);
      return;
    }
    onDone();
  }

  const chooserError =
    saveError ?? (phase.kind === "error" ? phase.message : null);

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          Restart model
        </h3>
        <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
          Keeps <code className="font-medium">{detail.recipe.code}</code> /{" "}
          <span className="font-medium">{detail.recipe.name}</span>. Replaces BOM content (and
          clears attachments on wipe/template/AI apply).
        </p>

        {phase.kind === "choose" || phase.kind === "error" ? (
          <ManufacturingModelsRestartChooser
            mode={mode}
            onMode={setMode}
            templateId={templateId}
            onTemplateId={setTemplateId}
            templateOptions={templateOptions}
            brief={brief}
            onBrief={setBrief}
            error={chooserError}
            onCancel={onClose}
            onContinue={() => void onContinue()}
          />
        ) : null}

        {phase.kind === "busy" ? (
          <p className="mt-6 text-sm text-gray-600 dark:text-zinc-300">{phase.label}</p>
        ) : null}

        {phase.kind === "review" ? (
          <div className="mt-4">
            <ManufacturingDictatedRecipeReview
              transcript={phase.transcript}
              draft={phase.draft}
              priorDraft={phase.prior}
              materials={materials}
              orgSlotLabels={orgSlotLabels}
              onDraftChange={(d) =>
                setPhase({
                  kind: "review",
                  draft: lockDraftIdentity(d, detail),
                  transcript: phase.transcript,
                  prior: phase.prior,
                })
              }
              onSave={() => void onSaveReview(phase.draft)}
              onClose={onClose}
              onRedictate={() => {
                setBrief(phase.transcript);
                setPhase({ kind: "choose" });
              }}
              saveBusy={saveBusy}
              saveError={saveError}
              mode="bom_correct"
              saveLabel="Apply restart"
              redictateLabel="Back"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
